/**
 * Shipments UI Plugin Routes
 *
 * Defines all routes for the shipments UI plugin.
 */

import { defineRoutes } from '@lognai/plugin-sdk';

/**
 * Plugin identifier
 */
export const pluginId = 'shipments';

/**
 * All routes for the shipments UI plugin
 */
export const routes = defineRoutes(pluginId, '/shipments', [
  // Landing index for /shipments (hidden from nav; the plugin group header
  // "Shipments" is provided by the app's PLUGIN_METADATA). Nav items below are
  // siblings under that group — no self-nesting — matching the orders plugin.
  {
    path: '/',
    component: () =>
      import('./pages/ShipmentListPage').then((m) => ({
        default: m.ShipmentListPage,
      })),
    name: 'Shipments',
    description: 'Manage shipments and freight',
    permissions: ['shipment:shipment:read'],
    nav: {
      icon: { name: 'Ship', size: 20 },
      showInNav: false,
      order: 40,
    },
  },

  // All Shipments sub-item — appears directly under the "Shipments" nav group
  {
    path: '/all',
    component: () =>
      import('./pages/ShipmentListPage').then((m) => ({
        default: m.ShipmentListPage,
      })),
    name: 'All Shipments',
    description: 'View all shipments',
    permissions: ['shipment:shipment:read'],
    nav: {
      icon: { name: 'Ship', size: 20 },
      showInNav: true,
      order: 1,
      // The detail and edit routes are siblings of this path, not children, so
      // they cannot be matched by prefix. Declare them so opening a shipment
      // keeps "All Shipments" marked as the page you are on.
      matchPaths: ['/shipments', '/shipments/:id', '/shipments/:id/edit'],
    },
  },

  // Container register: every box across shipments, flat and searchable.
  // Static path — must stay above `/:id`, which would otherwise capture it.
  {
    path: '/containers',
    component: () =>
      import('./pages/ContainerListPage').then((m) => ({
        default: m.ContainerListPage,
      })),
    name: 'Containers',
    description: 'All containers across shipments',
    permissions: ['shipment:shipment:read'],
    nav: {
      icon: { name: 'Container', size: 20 },
      showInNav: true,
      order: 0,
    },
  },

  // Global Shipment Radar (live FCL/LCL world map)
  {
    path: '/radar',
    component: () =>
      import('./pages/GlobalShipmentRadarPage').then((m) => ({
        default: m.GlobalShipmentRadarPage,
      })),
    name: 'Shipment Radar',
    description: 'Live FCL/LCL shipment radar',
    permissions: ['shipment:shipment:read'],
    nav: {
      icon: { name: 'Radar', size: 20 },
      showInNav: true,
      order: 0,
    },
  },

  // Static CO2 calculator (ad-hoc journey → GLEC estimate)
  {
    /**
     * Cross-shipment tracking subscriptions: which shipments are subscribed to
     * carrier tracking, and why some are not.
     *
     * Under Shipments rather than Admin because it is operational logistics
     * data, not organisational administration. The permission gate hides it from
     * users without the tracking read code — note that a company `admin`
     * holds the `*` wildcard and so always passes it, which is why the backend
     * derives SCOPE from the session's companyId rather than from permissions.
     */
    path: '/tracking-subscriptions',
    component: () =>
      import('./pages/TrackingSubscriptionsPage').then((m) => ({
        default: m.TrackingSubscriptionsPage,
      })),
    name: 'Tracking Subscriptions',
    description: 'Carrier tracking subscriptions — cancel, rebuild or force a sync',
    permissions: ['tracking:subscription:read'],
    nav: {
      icon: { name: 'Satellite', size: 20 },
      showInNav: true,
      order: 3,
    },
  },
  {
    path: '/co2-calculator',
    component: () =>
      import('./pages/Co2CalculatorPage').then((m) => ({
        default: m.Co2CalculatorPage,
      })),
    name: 'CO2 Calculator',
    description: 'Estimate CO2e for an ad-hoc journey',
    permissions: ['shipment:shipment:read'],
    nav: {
      icon: { name: 'Calculator', size: 20 },
      showInNav: true,
      order: 4,
    },
  },

  // Document configurations sub-item
  {
    path: '/settings/document-config',
    component: () =>
      import('./pages/DocumentConfigPage').then((m) => ({
        default: m.DocumentConfigPage,
      })),
    name: 'Document Configurations',
    description: 'Configure required documents per shipment type',
    permissions: ['shipment:document_config:read'],
    nav: {
      showInNav: true,
      order: 2,
    },
  },

  // Create new document config
  {
    path: '/settings/document-config/new',
    component: () =>
      import('./pages/DocumentConfigEditPage').then((m) => ({
        default: m.DocumentConfigEditPage,
      })),
    name: 'Create Document Configuration',
    description: 'Create a new document configuration',
    permissions: ['shipment:document_config:manage'],
    parentPath: '/settings/document-config',
    nav: {
      showInNav: false,
    },
  },

  // Edit document config by ID
  {
    path: '/settings/document-config/edit/:id',
    component: () =>
      import('./pages/DocumentConfigEditPage').then((m) => ({
        default: m.DocumentConfigEditPage,
      })),
    name: 'Edit Document Configuration',
    description: 'Edit a document configuration by ID',
    permissions: ['shipment:document_config:manage'],
    parentPath: '/settings/document-config',
    nav: {
      showInNav: false,
    },
  },

  // Milestone settings
  {
    path: '/settings/milestones',
    component: () =>
      import('./pages/MilestoneSettingsPage').then((m) => ({
        default: m.MilestoneSettingsPage,
      })),
    name: 'Milestone Settings',
    description: 'Configure milestone templates',
    permissions: ['shipment:milestone_config:read'],
    nav: {
      showInNav: true,
      order: 3,
    },
  },

  // Create new milestone config
  {
    path: '/settings/milestones/new',
    component: () =>
      import('./pages/MilestoneConfigEditPage').then((m) => ({
        default: m.MilestoneConfigEditPage,
      })),
    name: 'Create Milestone Configuration',
    description: 'Create a new milestone configuration',
    permissions: ['shipment:milestone_config:manage'],
    parentPath: '/settings/milestones',
    nav: {
      showInNav: false,
    },
  },

  // Edit milestone config by ID
  {
    path: '/settings/milestones/edit/:id',
    component: () =>
      import('./pages/MilestoneConfigEditPage').then((m) => ({
        default: m.MilestoneConfigEditPage,
      })),
    name: 'Edit Milestone Configuration',
    description: 'Edit a milestone configuration by ID',
    permissions: ['shipment:milestone_config:manage'],
    parentPath: '/settings/milestones',
    nav: {
      showInNav: false,
    },
  },

  // Milestone config editor for specific type/direction (legacy fallback)
  {
    path: '/settings/milestones/:shipmentType/:direction',
    component: () =>
      import('./pages/MilestoneConfigEditPage').then((m) => ({
        default: m.MilestoneConfigEditPage,
      })),
    name: 'Edit Milestone Configuration',
    description: 'Edit milestone configuration for a specific shipment type and direction',
    permissions: ['shipment:milestone_config:manage'],
    parentPath: '/settings/milestones',
    nav: {
      showInNav: false,
    },
  },

  // Charge catalog settings
  {
    path: '/settings/charges',
    component: () =>
      import('./pages/ChargeCatalogSettingsPage').then((m) => ({
        default: m.ChargeCatalogSettingsPage,
      })),
    name: 'Charge Catalog',
    description: 'Standardized logistics charges',
    permissions: ['shipment:charge_catalog:manage'],
    nav: {
      showInNav: true,
      order: 4,
    },
  },

  // Sailing schedules — a static path, so it sits above `/:id`.
  {
    path: '/schedules',
    component: () =>
      import('./pages/SailingSchedulesPage').then((m) => ({
        default: m.SailingSchedulesPage,
      })),
    name: 'Sailing Schedules',
    description: 'Upcoming sailings between ports',
    permissions: ['shipment:shipment:read'],
    nav: {
      showInNav: true,
      order: 3,
    },
  },

  // Custom fields used to be defined here, shipments only. They are company
  // fields now, defined once in Admin and switched on per module; the old path
  // stays mounted as a redirect so saved links keep working.
  {
    path: '/settings/custom-fields',
    component: () =>
      import('./pages/DDMovedRedirectPage').then((m) => ({
        default: m.CustomFieldsMovedPage,
      })),
    name: 'Custom Fields',
    description: 'Moved to Admin — forwards to /admin/custom-fields',
    permissions: ['shipment:shipment:read'],
    nav: {
      showInNav: false,
    },
  },

  // The D&D hub and contract-terms pages used to sit here, at /shipments/dd and
  // /shipments/settings/dd-terms. D&D is its own sidebar section now — see
  // `@lognai/plugin-dd-ui`. The per-shipment D&D tab stays on the detail page
  // below, rendered from that package.
  //
  // The two old paths stay mounted as redirects. Saved bookmarks, links in
  // tickets and alert emails sent before the move would otherwise land on
  // nothing, which reads as the screens having been withdrawn rather than
  // moved. Declared above `/:id` so `dd` is never captured as a shipment id.
  {
    path: '/dd',
    component: () =>
      import('./pages/DDMovedRedirectPage').then((m) => ({
        default: m.DDHubMovedPage,
      })),
    name: 'Demurrage & Detention',
    description: 'Moved to its own section — forwards to /dd',
    permissions: ['shipment:dd:read'],
    nav: {
      showInNav: false,
    },
  },
  {
    path: '/settings/dd-terms',
    component: () =>
      import('./pages/DDMovedRedirectPage').then((m) => ({
        default: m.DDTermsMovedPage,
      })),
    name: 'D&D Contract Terms',
    description: 'Moved to its own section — forwards to /dd/terms',
    permissions: ['shipment:dd:read'],
    nav: {
      showInNav: false,
    },
  },

  // Create shipment
  {
    path: '/new',
    component: () =>
      import('./pages/ShipmentCreatePage').then((m) => ({
        default: m.ShipmentCreatePage,
      })),
    name: 'Create Shipment',
    description: 'Create a new shipment',
    permissions: ['shipment:shipment:create'],
    parentPath: '/',
    nav: {
      showInNav: false,
    },
  },

  // Edit shipment (registered before /:id so it isn't shadowed)
  {
    path: '/:id/edit',
    component: () =>
      import('./pages/ShipmentEditPage').then((m) => ({
        default: m.ShipmentEditPage,
      })),
    name: 'Edit Shipment',
    description: 'Edit shipment details',
    permissions: ['shipment:shipment:update'],
    parentPath: '/',
    nav: {
      showInNav: false,
    },
  },

  // Shipment detail
  {
    path: '/:id',
    component: () =>
      import('./pages/ShipmentDetailPage').then((m) => ({
        default: m.ShipmentDetailPage,
      })),
    name: 'Shipment Details',
    description: 'View and manage shipment details',
    permissions: ['shipment:shipment:read'],
    parentPath: '/',
    nav: {
      showInNav: false,
    },
  },
]);

/**
 * Public routes (no auth) — a customer opens a shared tracking link without an
 * account. Registered separately, like freight-rfq-ui's magic-link quote page.
 */
export const publicRoutes = defineRoutes(pluginId, '/shipments', [
  {
    path: '/track/:token',
    component: () => import('./pages/PublicTrackingPage').then((m) => ({ default: m.PublicTrackingPage })),
    name: 'Shared shipment tracking',
    description: 'Tracking for one shipment, opened from a share link — no account required',
    requiresAuth: false,
    nav: { showInNav: false },
  },
]);
